import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { clinicalDiagramSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useCreateClinicalDiagramMutation, useFetchS3ObjectMutation } from "../../services/api";
import style from "./clinicalDiagram.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { ImageInput } from "../../components/UserInput";
import defaultImage from "../../assets/images/default-image.png";
import { getExtension } from "../../utils/common";
import type { ApiError, ClinicalDiagramFormValues } from "./clinicalDiagramTypes";
import { getApiErrorMessage, uploadClinicalDiagramFile } from "./clinicalDiagramUtils";

type FormState = ClinicalDiagramFormValues;

/**
 * Renders the Add Clinical Diagram form.
 *
 * The form collects diagram metadata, validates required fields, uploads
 * assets to S3 using pre-signed URLs, and submits a create mutation.
 *
 * Returns:
 *   JSX.Element: The Add Clinical Diagram page content.
 */
const AddClinicalDiagram = () => {
  const [createClinicalDiagram] = useCreateClinicalDiagramMutation();
  const [fetchS3Object] = useFetchS3ObjectMutation();
  const navigate = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [diagramError, setDiagramError] = useState(false)
  const [thumbnail, setThumbnail] = useState<File>();
  const [diagram, setDiagram] = useState<File>();

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(clinicalDiagramSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormState>({
    resolver: yupResolver(clinicalDiagramSchema),
    mode: "onChange",
  });

  const onSubmit = async (values: FormState) => {
    try {
      const { name } = values;
      // Both files are required; fail early to avoid incomplete uploads.
      if (thumbnail === undefined) {
        setThumbnailError(true);
        return;
      }
      if (diagram === undefined) {
        setDiagramError(true);
        return;
      }
      setIsLoading(true);
      let payload: AddClinicalDiagramPayload = {
        name: name,
      };
      if (thumbnail) {
        // Generate unique S3 key per upload to avoid overwriting existing assets.
        const fileExtension = getExtension(thumbnail?.name ?? "");
        const keyName = `static/clinical_diagram/clinical_diagram_${name}/thumbnail_${uuidv4()}.${fileExtension}`;
        const uploadedKey = await uploadClinicalDiagramFile({
          file: thumbnail,
          fetchS3Object,
          keyName,
        });
        if (uploadedKey) {
          payload = { ...payload, thumbnail_path: uploadedKey };
        }
      }
      if (diagram) {
        // Diagram asset is uploaded independently to allow partial retries without re-uploading the thumbnail.
        const fileExtension = getExtension(diagram?.name ?? "");
        const keyName = `static/clinical_diagram/clinical_diagram_${name}/diagram_${uuidv4()}.${fileExtension}`;
        const uploadedKey = await uploadClinicalDiagramFile({
          file: diagram,
          fetchS3Object,
          keyName,
        });
        if (uploadedKey) {
          payload = { ...payload, diagram_path: uploadedKey };
        }
      }
      await createClinicalDiagram(payload).unwrap();
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      setMessage({
        type: "error",
        text: getApiErrorMessage(error as ApiError, "Please enter valid details"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Clinical Diagram</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new clinical diagram to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="e.g Right Arm"
              label="Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
          </Box>
        </Box>
        <Box className={style.row} sx={{ flexDirection: "column", marginTop: "20px" }}>
          <Box component="label" htmlFor="thumbnail-input" sx={{ mb: -2, display: "flex", alignItems: "center" }} className={style.inputLabel}>
            Upload Thumbnail
            <Box component="span" sx={{ color: "red", ml: 0.7 }}>*</Box>
          </Box>
          <Box sx={{ width: "100%", }}>
            <ImageInput
              label="Thumbnail"
              required
              register={register}
              name="thumbnail"
              error={thumbnailError}
              onImageSelected={(images) => {
                setThumbnailError(false);
                setThumbnail(images[0]);
              }}
              height={"250px"}
              width={"100%"}
              borderRadius="6"
              defaultValue={defaultImage}
            />
          </Box>
        </Box>
        <Box className={style.row} sx={{ flexDirection: "column", marginTop: "5px" }}>
          <Box component="label" htmlFor="thumbnail-input" sx={{ mb: -2, display: "flex", alignItems: "center" }} className={style.inputLabel}>
            Upload Diagram
            <Box component="span" sx={{ color: "red", ml: 0.7 }}>*</Box>
          </Box>
          <Box sx={{ width: "100%", }}>
            <ImageInput
              label="Diagram"
              required
              register={register}
              name="diagram"
              error={diagramError}
              onImageSelected={(images) => {
                setDiagramError(false);
                setDiagram(images[0]);
              }}
              height={"250px"}
              width={"100%"}
              borderRadius="6"
              defaultValue={defaultImage}
            />
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigate(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AddClinicalDiagram;
